import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  first,
  map,
  merge,
  Subject,
  takeUntil,
} from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { CornerstoneService } from '@viewer/cornerstone.service';
import {
  BrushToolComponentKeyActionName,
  BrushToolComponentKeyActionNameList,
  CornerstoneTool,
  HotKey,
  HotKeySettings,
} from '@viewer/data';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import {
  PointerShape,
  IEditImageToolSettings,
  IBrushSettings,
  ToolActivationMethod,
} from '../data';
import { KeyInteractionService } from 'src/app/interaction';

@Component({
  selector: 'app-brush-tool',
  templateUrl: 'brush-tool.component.html',
  styleUrls: ['brush-tool.component.scss'],
})
export class BrushToolComponent
  extends BaseToolComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() settings: IEditImageToolSettings = {
    actionMode: 'button',
    label: 'Brush',
    displayIcon: true,
    disableTooltip: false,
    displayCorner: true,
    displayHotKey: true,
    hideMenuBackdrop: true,
    menuPosition: {
      x: 'before',
      y: 'above',
    },
  };
  @Input() useHotKeys = true;

  @Output() toolActivated = new EventEmitter<ToolActivationMethod>();

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
  @ViewChild('pointerSettingsMenu') pointerSettingsMenu!: any;

  public brushAction!: TriggerAction;
  public brushSettings!: IBrushSettings;
  public isBrushActive$ = new BehaviorSubject<boolean>(false);

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private _cornerstoneService: CornerstoneService,
    public stateManagerProjectService: StateManagerProjectService,
    private _elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService
  ) {
    super(stateManagerProjectService);
  }

  ngOnInit(): void {
    this.stateManagerProjectService.brushSettings$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((settings) => (this.brushSettings = settings));
    this.brushAction = new TriggerAction(
      this.settings.displayIcon ? 'brush' : '',
      this.settings.label,
      this._onActivateToolBtnClick.bind(this)
    );

    this.brushAction.isEnable = () =>
      combineLatest([
        this.isDisabled$,
        this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      ]).pipe(
        map(
          ([isDisabled, isDisabledAllToolsForChangeMask]) =>
            !isDisabled && !isDisabledAllToolsForChangeMask
        )
      );

    this.stateManagerProjectService.currentStateOfToolMenu$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentStateOfToolMenu) => {
        if (currentStateOfToolMenu !== 'BRUSH' && this.menuTrigger) {
          this.menuTrigger.closeMenu();
        }
      });

    this.stateManagerProjectService.currentTool$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentTool) => {
        this.isBrushActive$.next(this._checkBrushActivated(currentTool));
      });

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }

    this._cornerstoneService.closeOpenedMenu$.pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => this.menuTrigger.closeMenu());
  }

  ngAfterViewInit(): void {
    if (this.settings.actionMode === 'button') {
      super.initializeMenuBackdrop(this.menuTrigger, this._elementRef);
    }

    if (this.settings.openMenu) {
      this.menuTrigger.openMenu();
    }

    if (this.settings.closeMenuCondition$) {
      this.settings.closeMenuCondition$
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((toClose) => {
          if (toClose) {
            this.menuTrigger.closeMenu();
          }
        });
    }

    if (this.useHotKeys) {
      merge(
        this.menuTrigger.menuOpened.pipe(map(() => true)),
        this.menuTrigger.menuClosed.pipe(map(() => false))
      )
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((isMenuOpened) => {
          this._keyInteractionService.setActivateState<BrushToolComponentKeyActionName>(
            'ExitBrushMenu',
            isMenuOpened
          );
        });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...BrushToolComponentKeyActionNameList,
      ]);
    }
  }

  public onSizeChanged(size: number): void {
    this.stateManagerProjectService.brushSettings = {
      ...this.brushSettings,
      size: size,
    };
  }

  public onShapeChanged(shape: PointerShape): void {
    this.stateManagerProjectService.brushSettings = {
      ...this.brushSettings,
      shape: shape,
    };
    this._activateTool();
  }

  public onBrushActionContextMenu(event: MouseEvent): void {
    this.brushAction
      .isEnable()
      .pipe(first())
      .subscribe((isEnable) => {
        if (!isEnable) {
          return;
        }

        event.preventDefault();
        this.menuTrigger.toggleMenu();

        if (
          this._checkBrushActivated(this.stateManagerProjectService.currentTool)
        ) {
          return;
        }

        this._onActivateToolBtnClick(event);
      });
  }

  private _onActivateToolBtnClick(event?: MouseEvent): void {
    if (!this.menuTrigger) {
      return;
    }

    if (!event && this.settings.actionMode === 'contextmenu') {
      this.toolActivated.emit('LMB');
    }

    if (this.isBrushActive$.getValue()) {
      this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
      return;
    }

    this._activateTool();
    this._cornerstoneService.setBrushSize(this.brushSettings.size);
  }

  private _activateTool(): void {
    this._cornerstoneService.toolActivate(
      `${this.brushSettings.shape}Brush` as CornerstoneTool
    );
  }

  public updateComponent(): void {
    this._cdr.markForCheck();
  }

  private _checkBrushActivated(currentTool: CornerstoneTool | null): boolean {
    if (!currentTool) {
      return false;
    }

    return [CornerstoneTool.CircleBrush, CornerstoneTool.SquareBrush].includes(
      currentTool
    );
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.B,
        new HotKeySettings<BrushToolComponentKeyActionName>({
          keyActionName: 'BrushActivate',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this._stateManagerProjectService.isDisabledAllToolsForChangeMask) {
          return;
        }

        this.brushAction.trigger();
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<BrushToolComponentKeyActionName>({
          keyActionName: 'ExitBrushMenu',
          priority: 3,
          isActive: !!this.settings.openMenu,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.menuTrigger.closeMenu();
      });
  }
}
